import React, { useContext } from "react";
import { StandardCase } from "@src/lms/jelly/extensions/layouts/standard-case/StandardCase";
import { useUi } from "@jelly/lesson";
import { Accordion, AccordionItem } from "@src/lms/jelly/extensions/ui";
import { useIntl } from "react-intl";
import { AccordionContext } from "@src/lms/jelly/extensions/ui/accordion/AccordionContext";

import { messages } from "./Workflow.messages";
import styles from "./Workflow.module.scss";

import { SignalAndDataFlows } from "./components/SignalAndDataFlows";
import { OrganisingSession } from "./components/OrganisingSession";
import { Editing } from "./components/Editing";
import { PanningAndObjectPlacement } from "./components/PanningAndObjectPlacement";
import { QualityControl } from "./components/QualityControl";
import { FinalMixing } from "./components/FinalMixing";
import { PrintmasteringAndPackaging } from "./components/PrintmasteringAndPackaging";
import { nl2p } from "@src/utils/nl2p";

interface Workflow {
  title: string;
}

export const Workflow: React.FC<Workflow> = ({ title }) => {
  return (
    <Accordion allowMultiOpen={false} defaultOpen={["signal-and-data-flows"]}>
      <WorkflowContent title={title} />
    </Accordion>
  );
};

const WorkflowContent: React.FC<Workflow> = ({ title }) => {
  const ui = useUi();
  const { formatMessage } = useIntl();
  const context = useContext(AccordionContext);

  const accordion = (
    <div className={styles.accordion}>
      <ui.Scrollbars>
        <AccordionItem
          id={"signal-and-data-flows"}
          title={formatMessage(messages.accordionItem1Title)}
          unmountChildrenWhenHidden
        >
          <p>{nl2p(formatMessage(messages.accordionItem1Description))}</p>
        </AccordionItem>
        <AccordionItem
          id={"organising-the-session"}
          title={formatMessage(messages.accordionItem2Title)}
          unmountChildrenWhenHidden
        >
          <p>{nl2p(formatMessage(messages.accordionItem2Description))}</p>
        </AccordionItem>
        <AccordionItem id={"editing"} title={formatMessage(messages.accordionItem3Title)} unmountChildrenWhenHidden>
          <p>{nl2p(formatMessage(messages.accordionItem3Description))}</p>
        </AccordionItem>
        <AccordionItem
          id={"panning-and-object-placement"}
          title={formatMessage(messages.accordionItem4Title)}
          unmountChildrenWhenHidden
        >
          <p>{nl2p(formatMessage(messages.accordionItem4Description))}</p>
        </AccordionItem>
        <AccordionItem
          id={"quality-control"}
          title={formatMessage(messages.accordionItem5Title)}
          unmountChildrenWhenHidden
        >
          <p>{nl2p(formatMessage(messages.accordionItem5Description))}</p>
        </AccordionItem>
        <AccordionItem
          id={"final-mixing"}
          title={formatMessage(messages.accordionItem6Title)}
          unmountChildrenWhenHidden
        >
          <p>{nl2p(formatMessage(messages.accordionItem6Description))}</p>
        </AccordionItem>
        <AccordionItem
          id={"printmastering-and-packaging"}
          title={formatMessage(messages.accordionItem7Title)}
          unmountChildrenWhenHidden
        >
          <p>{nl2p(formatMessage(messages.accordionItem7Description))}</p>
        </AccordionItem>
      </ui.Scrollbars>
    </div>
  );

  const renderRightComponent = (): React.ReactNode => {
    if (context.openIds.includes("signal-and-data-flows")) {
      return <SignalAndDataFlows />;
    }
    if (context.openIds.includes("organising-the-session")) {
      return <OrganisingSession />;
    }
    if (context.openIds.includes("editing")) {
      return <Editing />;
    }
    if (context.openIds.includes("panning-and-object-placement")) {
      return <PanningAndObjectPlacement />;
    }
    if (context.openIds.includes("quality-control")) {
      return <QualityControl />;
    }
    if (context.openIds.includes("final-mixing")) {
      return <FinalMixing />;
    }
    if (context.openIds.includes("printmastering-and-packaging")) {
      return <PrintmasteringAndPackaging />;
    }
    return null;
  };

  return (
    <StandardCase title={title} leftComponent={accordion}>
      <div className={styles.rightComponent}>{renderRightComponent()}</div>
    </StandardCase>
  );
};
